import { stylesToV3 } from "components/common/stylesToV3";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { TlfContext } from "contexts/TlfContext";
import { EditorV3, IEditorV3, stringToV3 } from "editor-v3";
import { eStyleTlfType } from "functions/api/styles/getAllStyles";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { useContext } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { UPDATE_TLF } from "../../contexts/tlf/interface";
import { eHierarchyLevel } from "../../functions/api/enums";

/**
 * Editor for standards notes attached to a TLF item with access controlled by hierarchy.
 *
 * @returns A JSX element rendering the standards notes editor or an empty fragment.
 */
export const StandardsNotes = (): React.ReactNode => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const tlfData: TlfItemReadDto | null =
    tlfContext && tlfContext.tlfData && "standardsNotes" in tlfContext.tlfData
      ? (tlfContext.tlfData as TlfItemReadDto)
      : null;

  const standardNotes: IEditorV3 =
    (tlfContext?.tlfData as TlfItemReadDto)?.standardsNotes ??
    // istanbul ignore next
    stringToV3("");

  return !hierarchyContext || !hierarchyContext.hierarchy || !tlfContext || !tlfData ? null : (
    <Container>
      <Row>
        <Col>
          <h4>Standards notes</h4>
          <div className="notes-box">
            <EditorV3
              id="standardsnotes"
              allowNewLine
              customStyleMap={stylesToV3(appSettings.styles, eStyleTlfType.Template)}
              debounceMilliseconds={-1}
              editable={
                !tlfContext.readOnly &&
                [eHierarchyLevel.Corporate, eHierarchyLevel.TA, eHierarchyLevel.Project].includes(
                  hierarchyContext.hierarchy.level,
                )
              }
              input={standardNotes}
              setObject={(ret) => {
                tlfContext.dispatch({
                  operation: UPDATE_TLF,
                  tlfData: { standardsNotes: ret },
                  calledFrom: "StandardsNotes:setValue",
                });
              }}
              style={{
                ...appSettings.defaultTableStyle,
                minHeight: "120px",
                height: "120px",
                border: "none",
                backgroundColor:
                  !tlfContext.readOnly &&
                  [eHierarchyLevel.Corporate, eHierarchyLevel.TA, eHierarchyLevel.Project].includes(
                    hierarchyContext?.hierarchy?.level,
                  )
                    ? undefined
                    : "rgba(211, 211, 211, 0.16)",
              }}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

StandardsNotes.displayName = "StandardsNotes";
